import { Injectable, Signal, signal } from '@angular/core';
import { environment } from '@env/environment';
import {
  AppFeatureFlag,
  FeatureFlags,
} from '@shared/types/feature-flags.types';

/**
 * Global feature flag service.
 * Provides typed flag checks and supports local runtime overrides.
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private readonly environmentFlags: FeatureFlags = environment.features;
  private readonly flagsSignal = signal<FeatureFlags>(this.environmentFlags);

  /**
   * Checks whether a feature flag is enabled.
   * @param featureFlag - Flag key to evaluate.
   * @returns True when the flag is enabled.
   */
  isEnabled(featureFlag: AppFeatureFlag): boolean {
    return this.flagsSignal()[featureFlag];
  }

  /**
   * Returns a read-only signal with all flag values.
   * @returns Read-only feature flags signal.
   */
  getAllFlags(): Signal<FeatureFlags> {
    return this.flagsSignal.asReadonly();
  }

  /**
   * Merges runtime overrides with current flag state.
   * @param overrides - Partial map of flags to update.
   * @returns void
   */
  setFlags(overrides: Partial<FeatureFlags>): void {
    this.flagsSignal.update(currentFlags => ({
      ...currentFlags,
      ...overrides,
    }));
  }

  /**
   * Restores flags to environment defaults.
   * @returns void
   */
  resetToEnvironmentDefaults(): void {
    this.flagsSignal.set(this.environmentFlags);
  }
}
